/**
 * Clusters of pastel crystals: faceted prisms fanning out of a rocky base,
 * with a lit left face, a shaded right face and (optionally) twinkling glints
 * on their tips.
 */
import type { CrystalsLayer } from '@dropstack/core';
import { spreadPositions, type SceneSize } from '../layout.ts';
import { hash01, range, rngFor } from '../noise.ts';
import { bodyGradient, circleBlob, mix, pick, rgba, rimOf, shade, strokeRim, twinkle, vGradient, type Ctx, type Point } from './draw.ts';
import type { PaintEnv, Painter } from './types.ts';

interface Crystal {
  base: Point;
  /** Direction the crystal points (radians, −π/2 = straight up). */
  angle: number;
  length: number;
  width: number;
  color: string;
}

interface Cluster {
  x: number;
  y: number;
  s: number;
  /** Back to front. */
  crystals: Crystal[];
}

function planClusters(layer: CrystalsLayer, env: PaintEnv): Cluster[] {
  const { size, seed } = env;
  const rng = rngFor(seed, 1);
  const colors = layer.colors.length ? layer.colors : ['#c5b3ff'];
  const xs = spreadPositions(layer.count, layer.spread ?? [0, 1], (i) => hash01(seed + 2, i));
  return xs.map((fx, k): Cluster => {
    const x = fx * size.width;
    const y = layer.baseline * size.height + size.unit * range(rng, -0.006, 0.006);
    const s = layer.size * size.unit * range(rng, 0.75, 1.2);
    const n = 3 + Math.floor(rng.next() * 3);
    const crystals = Array.from({ length: n }, (_, j): Crystal => {
      const u = n === 1 ? 0 : j / (n - 1) - 0.5; // −0.5 … 0.5 across the cluster
      const length = s * (1 - Math.abs(u) * 0.9) * range(rng, 0.75, 1);
      return {
        base: { x: x + u * s * 0.45, y },
        angle: -Math.PI / 2 + u * 1.1 + range(rng, -0.1, 0.1),
        length,
        width: length * range(rng, 0.26, 0.34),
        color: pick(colors, k + j),
      };
    });
    // Short outer crystals behind, the tall middle one in front.
    crystals.sort((a, b) => a.length - b.length);
    return { x, y, s, crystals };
  });
}

/** Tip of a crystal in screen space. */
function tipOf(c: Crystal): Point {
  return { x: c.base.x + Math.cos(c.angle) * c.length, y: c.base.y + Math.sin(c.angle) * c.length };
}

function drawCrystal(ctx: Ctx, c: Crystal, size: SceneSize): void {
  const L = c.length;
  const W = c.width;
  ctx.save();
  ctx.translate(c.base.x, c.base.y);
  ctx.rotate(c.angle + Math.PI / 2);
  // Local frame: base at (0, 0), tip at (0, −L).
  const outline = () => {
    ctx.beginPath();
    ctx.moveTo(-W / 2, W * 0.1);
    ctx.lineTo(-W / 2, -L * 0.74);
    ctx.lineTo(0, -L);
    ctx.lineTo(W / 2, -L * 0.74);
    ctx.lineTo(W / 2, W * 0.1);
    ctx.closePath();
  };
  outline();
  ctx.fillStyle = vGradient(ctx, -L, 0, [shade(c.color, 0.45), c.color, shade(c.color, -0.05)]);
  ctx.fill();
  // Shaded right face and a pale top facet.
  ctx.beginPath();
  ctx.moveTo(W * 0.12, W * 0.1);
  ctx.lineTo(W * 0.12, -L * 0.74);
  ctx.lineTo(0, -L);
  ctx.lineTo(W / 2, -L * 0.74);
  ctx.lineTo(W / 2, W * 0.1);
  ctx.closePath();
  ctx.fillStyle = rgba(shade(c.color, -0.3), 0.45);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-W / 2, -L * 0.74);
  ctx.lineTo(0, -L);
  ctx.lineTo(W * 0.12, -L * 0.74);
  ctx.closePath();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
  ctx.fill();
  // Facet edges.
  ctx.beginPath();
  ctx.moveTo(W * 0.12, W * 0.1);
  ctx.lineTo(W * 0.12, -L * 0.74);
  ctx.lineTo(-W / 2, -L * 0.74);
  ctx.strokeStyle = rgba(shade(c.color, -0.35), 0.45);
  ctx.lineWidth = Math.max(1, W * 0.04);
  ctx.lineJoin = 'round';
  ctx.stroke();
  outline();
  strokeRim(ctx, rimOf(c.color), size);
  // Glassy highlight strip on the lit face.
  ctx.beginPath();
  ctx.moveTo(-W * 0.36, -L * 0.1);
  ctx.lineTo(-W * 0.36, -L * 0.66);
  ctx.lineTo(-W * 0.2, -L * 0.72);
  ctx.lineTo(-W * 0.2, -L * 0.14);
  ctx.closePath();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
  ctx.fill();
  ctx.restore();
}

export const crystalsPainter: Painter<CrystalsLayer> = {
  extent: (layer, { size }) => ({
    top: layer.baseline * size.height - layer.size * size.unit * 1.3,
    bottom: layer.baseline * size.height + layer.size * size.unit * 0.3,
  }),
  still(ctx, layer, env) {
    const { size } = env;
    for (const cl of planClusters(layer, env)) {
      for (const c of cl.crystals) drawCrystal(ctx, c, size);
      // Rocky base the crystals grow from.
      const rock = mix(cl.crystals[0]?.color ?? '#c5b3ff', '#8f86a8', 0.55);
      const rocks = [-0.32, -0.1, 0.14, 0.34].map((u, i) => ({
        x: cl.x + u * cl.s,
        y: cl.y + cl.s * 0.06,
        r: cl.s * (0.1 + 0.05 * hash01(env.seed + 9, i)),
      }));
      circleBlob(ctx, rocks, bodyGradient(ctx, rock, cl.y - cl.s * 0.1, cl.y + cl.s * 0.15), rimOf(rock), size);
    }
  },
  moving(ctx, layer, env) {
    if (!layer.glint) return;
    const { size, time } = env;
    planClusters(layer, env).forEach((cl, k) => {
      cl.crystals.forEach((c, j) => {
        const tip = tipOf(c);
        const a = Math.max(0, Math.sin(time * (0.8 + 0.3 * ((k + j) % 3)) + k * 2.1 + j * 1.3)) ** 4;
        twinkle(ctx, tip.x, tip.y, c.width * 0.9, '#ffffff', a);
      });
    });
  },
  animated: (layer) => layer.glint === true,
};
